import React from 'react';
import * as Icons from 'lucide-react';

const FeatureSection = ({ features }) => {
  if (!features || features.length === 0) return null;

  const colors = [
    {
      card: 'border-teal-500/25 shadow-teal-500/10',
      badge: 'text-teal-300 bg-teal-500/10 border-teal-500/25',
      icon: 'text-teal-400 bg-teal-500/10 border-teal-500/25',
      dot: 'bg-teal-400 shadow-teal-400/40',
      line: 'from-teal-400',
    },
    {
      card: 'border-amber-500/25 shadow-amber-500/10',
      badge: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
      icon: 'text-amber-400 bg-amber-500/10 border-amber-500/25',
      dot: 'bg-amber-400 shadow-amber-400/40',
      line: 'from-amber-400',
    },
    {
      card: 'border-red-500/25 shadow-red-500/10',
      badge: 'text-red-300 bg-red-500/10 border-red-500/25',
      icon: 'text-red-400 bg-red-500/10 border-red-500/25',
      dot: 'bg-red-400 shadow-red-400/40',
      line: 'from-red-400',
    },
    {
      card: 'border-purple-500/25 shadow-purple-500/10',
      badge: 'text-purple-300 bg-purple-500/10 border-purple-500/25',
      icon: 'text-purple-400 bg-purple-500/10 border-purple-500/25',
      dot: 'bg-purple-400 shadow-purple-400/40',
      line: 'from-purple-400',
    },
  ];

  const visibleFeatures = features.slice(0, 4);

  return (
    <section className="relative py-8 md:py-12">
      <style>
        {`
          @keyframes airsFlowY {
            0% { transform: translateY(-120%); opacity: 0; }
            15% { opacity: 1; }
            85% { opacity: 1; }
            100% { transform: translateY(520%); opacity: 0; }
          }

          @keyframes airsFlowX {
            0% { transform: translateX(-120%); opacity: 0; }
            15% { opacity: 1; }
            85% { opacity: 1; }
            100% { transform: translateX(520%); opacity: 0; }
          }
        `}
      </style>
      <div className="relative mb-16 text-center max-w-3xl mx-auto">
        <span className="inline-flex rounded-full border border-teal-500/25 bg-teal-500/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-teal-400">
          AIRS Core Capabilities
        </span>

        <h2 className="mt-6 text-3xl md:text-5xl font-black text-white tracking-tight">
          Autonomous security intelligence for real infrastructure.
        </h2>

        <p className="mt-5 text-slate-400 text-base md:text-lg leading-relaxed">
          AIRS connects telemetry, AI reasoning, autonomous response, and rollback-aware operator control into one continuous protection workflow.
        </p>
      </div>

      <div className="relative space-y-10 md:space-y-14">
        <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px overflow-hidden">
          <div className="absolute inset-0 border-l border-dashed border-white/10" />
          <div className="absolute left-0 top-0 h-24 w-px bg-gradient-to-b from-transparent via-teal-400/70 to-transparent animate-[airsFlowY_2.8s_linear_infinite]" />
        </div>

        {visibleFeatures.map((f, i) => {
          const isEven = i % 2 === 0;
          const IconComponent = Icons[f.icon_name] || Icons.Shield;
          const theme = colors[i % colors.length];

          return (
            <div key={f.id || i} className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              <div className={`hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border border-white/15 bg-[#020617] items-center justify-center shadow-xl`}>
                <span className={`h-3 w-3 rounded-full ${theme.dot} shadow-lg animate-pulse`} />
              </div>

              <article
                className={`
                  relative overflow-hidden rounded-[2rem] border ${theme.card}
                  bg-white/[0.035] p-6 md:p-8 shadow-2xl backdrop-blur-xl
                  hover:-translate-y-1 hover:bg-white/[0.055] transition-all duration-300
                  ${isEven ? 'md:col-start-1 md:mr-10' : 'md:col-start-2 md:ml-10'}
                `}
              >
                <div className="" />
                <div className="absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] hover:translate-x-[100%] transition-transform duration-1000" />

                <div className="relative">
                  <div className="flex items-start justify-between gap-5">
                    <div className="flex items-center gap-4">
                      <div className={`h-14 w-14 rounded-2xl border ${theme.icon} flex items-center justify-center shrink-0`}>
                        <IconComponent className="w-7 h-7" />
                      </div>

                      <div>
                        <span className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${theme.badge}`}>
                          {f.subtitle || 'Feature'}
                        </span>

                        <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-600">
                          Module {String(i + 1).padStart(2, '0')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <h2 className="mt-7 text-3xl md:text-4xl font-black text-white tracking-tight leading-tight">
                    {f.title}
                  </h2>

                  <p className="mt-5 text-slate-400 text-base md:text-lg leading-relaxed">
                    {f.description}
                  </p>

                  <div className="mt-7 flex items-center gap-3">
                    <div className={`h-1 w-14 rounded-full bg-gradient-to-r ${theme.line} to-transparent`} />
                    <div className="h-1 w-5 rounded-full bg-slate-700" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Protected Layer
                    </span>
                  </div>
                </div>
              </article>

              <div className={`${isEven ? 'hidden md:block md:col-start-2' : 'hidden md:block md:col-start-1 md:row-start-1'}`}>
                <div className="relative h-full min-h-[120px]">
                  <div
                    className={`
                      absolute top-1/2 h-px w-full overflow-hidden
                      ${isEven ? 'left-0' : 'right-0'}
                    `}
                  >
                    <div className="absolute inset-0 border-t border-dashed border-white/10" />
                    <div className="absolute top-0 h-px w-20 bg-gradient-to-r from-transparent via-teal-400/70 to-transparent animate-[airsFlowX_2.2s_linear_infinite]" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FeatureSection;